import { useEffect, useState } from "react";
import { NavigateOptions, To, useNavigate } from "react-router-dom";
import {
	AcademicCapIcon,
	ArrowUpTrayIcon,
	BuildingLibraryIcon,
	ChartBarIcon,
	Cog6ToothIcon,
	EyeIcon,
	LightBulbIcon,
	PencilIcon,
	PresentationChartLineIcon,
	QuestionMarkCircleIcon,
	UserCircleIcon,
	UserPlusIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { handleEnterSpaceKey } from "../../app/keyboardUtils";
import { isAdminRole, isHigherThanDistrictAdmin, UserRole } from "../../app/userSlice";
import SupportLink from "../../components/SupportLink";

interface LinkModel {
	label: string;
	icon: React.ElementType;
	to: To;
	options?: NavigateOptions;
}

const teacherShortcuts: LinkModel[] = [
	{
		label: "Create a New Prompt",
		icon: PencilIcon,
		to: "/writing/prompt",
	},
	{
		label: "Assign a Lesson",
		icon: LightBulbIcon,
		to: "/lessons",
		options: { state: { openDialog: true } },
	},
	{
		label: "Add Students to Course",
		icon: UserPlusIcon,
		to: "/course",
		options: { state: { openAddStudentDialog: true } },
	},
	{
		label: "Edit Course Details",
		icon: Cog6ToothIcon,
		to: "/course",
		options: { state: { openCourseInfoDialog: true } },
	},
	{
		label: "View Draft History",
		icon: PresentationChartLineIcon,
		to: "/reports/course/progress",
		options: {
			state: {
				section: "essay-draft-history",
			},
		},
	},
	{
		label: "View Usage Report",
		icon: ChartBarIcon,
		to: "/reports/course/usage",
	},
	{
		label: "Switch Course",
		icon: AcademicCapIcon,
		to: "/courses",
	},
	{
		label: "View as Student",
		icon: EyeIcon,
		to: "/impersonation",
	},
];

const studentShortcuts: LinkModel[] = [
	{
		label: "Write to a Practice Prompt",
		icon: PencilIcon,
		to: "/writing",
		options: {
			state: {
				section: "PracticePrompts",
			},
		},
	},
	{
		label: "View Draft History",
		icon: PresentationChartLineIcon,
		to: "/reports/student/progress",
		options: {
			state: {
				section: "essay-draft-history",
			},
		},
	},
	{
		label: "View Usage Report",
		icon: ChartBarIcon,
		to: "/reports/student/usage",
	},
	{
		label: "Switch Course",
		icon: AcademicCapIcon,
		to: "/courses",
	},
];

const adminShortcuts: LinkModel[] = [
	{
		label: "Create a New Prompt",
		icon: PencilIcon,
		to: "/prompts/prompt",
	},
	{
		label: "Add New User",
		icon: UserPlusIcon,
		to: "/users",
		options: { state: { openCreateUserDialog: true } },
	},
];

export default function ShortcutMenu(props: { canSeeRosterImportShortcut?: boolean }) {
	const {
		role,
		isTeachersWriterAccount,
		userImageUploadDisabled,
		districts,
		schools,
		district,
		school,
	} = useAppSelector((state) => state.user);

	const [shortcuts, setShortcuts] = useState<LinkModel[]>([]);

	useEffect(() => {
		if (role === UserRole.Writer) {
			let newShortcutsToAdd = [];

			if (!userImageUploadDisabled) {
				newShortcutsToAdd.push({
					label: "Change Avatar",
					icon: UserCircleIcon,
					to: "/myAccount",
				});
			}

			if (isTeachersWriterAccount) {
				newShortcutsToAdd.push({
					label: "View as Teacher",
					icon: EyeIcon,
					to: "/impersonation",
				});
			}
			const newShortcuts = [...studentShortcuts, ...newShortcutsToAdd];
			setShortcuts(newShortcuts);
		} else if (!isAdminRole(role)) {
			setShortcuts(teacherShortcuts);
		} else {
			let newShortcutsToAdd = [];

			if (role && isHigherThanDistrictAdmin(role)) {
				newShortcutsToAdd.push({
					label: "Create New District",
					icon: BuildingLibraryIcon,
					to: "/districts",
					options: { state: { openCreateDistrictDialog: true } },
				});

				newShortcutsToAdd.push({
					label: "Add New School",
					icon: AcademicCapIcon,
					to: "/schools",
					options: { state: { openCreateSchoolDialog: true } },
				});
			}

			if (props.canSeeRosterImportShortcut) {
				const link =
					role === UserRole.SchoolAdmin
						? `/schools/rosterImport?schoolId=${school?.id}&schoolName=${school?.name}`
						: role === UserRole.DistrictAdmin
							? `/schools/rosterImport?districtId=${district?.id}`
							: "/schools/rosterImport";

				newShortcutsToAdd.push({
					label: "Roster Import",
					icon: ArrowUpTrayIcon,
					to: link,
				});
			}

			if (role === UserRole.DistrictAdmin && districts && districts.length > 1) {
				newShortcutsToAdd.push({
					label: "Switch District",
					icon: AcademicCapIcon,
					to: "/selectDistrict",
				});
			}
			if (role === UserRole.SchoolAdmin && schools && schools.length > 1) {
				newShortcutsToAdd.push({
					label: "Switch School",
					icon: AcademicCapIcon,
					to: "/selectSchool",
				});
			}

			if (!userImageUploadDisabled) {
				newShortcutsToAdd.push({
					label: "Change Avatar",
					icon: UserCircleIcon,
					to: "/myAccount",
				});
			}
			const newShortcuts = [...adminShortcuts, ...newShortcutsToAdd];
			setShortcuts(newShortcuts);
		}
	}, [props]);

	return (
		<div className="mt-4 overflow-hidden rounded-xl border border-gray-200 max-w-2xl lg:max-w-none">
			<div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6">
				<p className="text-lg font-medium leading-6 text-gray-900">Shortcuts</p>
			</div>
			<ul className="divide-y divide-gray-100 px-6 py-1 text-sm leading-6">
				{shortcuts.map((item, index) => {
					return <ShortcutItem key={index} item={item} />;
				})}
				<li>
					<SupportLink
						id="shortcuts-support-center"
						key="support-center"
						className="text-black">
						<div className="flex items-center gap-x-4 py-3">
							<div className="w-1/12">
								<QuestionMarkCircleIcon className="h-5 w-5" />
							</div>
							<p className="w-11/12 pl-2">Help</p>
						</div>
					</SupportLink>
				</li>
			</ul>
		</div>
	);
}

interface ShortcutItemProps {
	item: LinkModel;
}

function ShortcutItem(props: ShortcutItemProps) {
	const navigate = useNavigate();

	function handleNavigate() {
		navigate(props.item.to, props.item.options);
	}

	return (
		<li
			tabIndex={0}
			id={props.item.label}
			className="flex items-center gap-x-4 py-3 hover:cursor-pointer"
			onClick={handleNavigate}
			onKeyDown={(e) => handleEnterSpaceKey(e, handleNavigate)}>
			<div className="w-1/12">
				<props.item.icon className="size-5" />
			</div>
			<p className="w-11/12 pl-2">{props.item.label}</p>
		</li>
	);
}
